import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BellRing, Camera, CameraOff, CheckCircle2, ClipboardCheck, Download, LogOut, PartyPopper, RefreshCcw, ScanLine, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE_URL, formatApiError } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const formatDate = (value) => (value ? new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—");

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registrations, setRegistrations] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [checkinCode, setCheckinCode] = useState("");
  const [checkinResult, setCheckinResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const scannerRef = useRef(null);

  const loadRegistrations = useCallback(async () => {
    const { data } = await api.get("/admin/registrations");
    setRegistrations(data);
  }, []);

  const loadWaitlist = useCallback(async () => {
    const { data } = await api.get("/admin/waitlist");
    setWaitlist(data);
  }, []);

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, [location.state]);

  useEffect(() => {
    if (user) {
      loadRegistrations().catch(() => toast.error("Registrierungen konnten nicht geladen werden."));
      loadWaitlist().catch(() => toast.error("Warteliste konnte nicht geladen werden."));
    }
  }, [user, loadRegistrations, loadWaitlist]);

  const stats = useMemo(() => ({
    total: registrations.length,
    paid: registrations.filter((item) => item.payment_status === "paid_at_door").length,
    checked: registrations.filter((item) => item.checked_in).length,
  }), [registrations]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUser(data);
      toast.success("Veranstalter-Anmeldung erfolgreich.");
    } catch (error) {
      toast.error(formatApiError(error, "Anmeldung fehlgeschlagen."));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setRegistrations([]);
  };

  const runCheckin = async (code) => {
    try {
      const { data } = await api.post("/admin/checkin", { code });
      setCheckinResult(data);
      if (data.already_checked_in) toast.warning("Dieser Gast war bereits eingecheckt.");
      else toast.success(`Einlass bestätigt. €${data.registration.price_eur} am Eingang erfasst.`);
      setCheckinCode("");
      await loadRegistrations();
    } catch (error) {
      toast.error(formatApiError(error, "QR-Code konnte nicht geprüft werden."));
    }
  };

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch {}
      try { scanner.clear(); } catch {}
    }
    setScannerActive(false);
  }, []);

  const startScanner = async () => {
    if (scannerActive || scannerRef.current) return;
    setScannerError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setScannerError("Diese Kamera wird vom Browser nicht unterstützt. Bitte den Code manuell eingeben.");
      return;
    }
    try {
      const scanner = new Html5Qrcode("qr-camera-reader");
      scannerRef.current = scanner;
      setScannerActive(true);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          await stopScanner();
          await runCheckin(decodedText);
        },
        () => {},
      );
    } catch {
      scannerRef.current = null;
      setScannerActive(false);
      setScannerError("Kamera konnte nicht gestartet werden. Bitte Berechtigung prüfen oder manuell eingeben.");
    }
  };

  const submitCheckin = (event) => {
    event.preventDefault();
    runCheckin(checkinCode);
  };

  useEffect(() => () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      try { scannerRef.current.clear(); } catch {}
    }
  }, []);

  const markPaid = async (code) => {
    try {
      await api.post(`/admin/registrations/${code}/mark-paid`);
      toast.success("Zahlung am Eingang bestätigt.");
      await loadRegistrations();
    } catch (error) {
      toast.error(formatApiError(error, "Zahlungsstatus konnte nicht aktualisiert werden."));
    }
  };

  const sendBoardingPass = async (code) => {
    try {
      await api.post(`/admin/registrations/${code}/send-boarding-pass`);
      toast.success("QR-Ticket wurde gesendet.");
      await loadRegistrations();
    } catch (error) {
      toast.error(formatApiError(error, "QR-Ticket konnte nicht gesendet werden."));
    }
  };

  const notifyWaitlist = async () => {
    try {
      const { data } = await api.post("/admin/waitlist/notify");
      toast.success(`Warteliste benachrichtigt: ${data.sent} gesendet, ${data.failed} fehlgeschlagen.`);
      await loadWaitlist();
    } catch (error) {
      toast.error(formatApiError(error, "Warteliste konnte nicht benachrichtigt werden."));
    }
  };

  const sendEventReminder = async () => {
    setReminderLoading(true);
    try {
      const { data } = await api.post("/admin/registrations/send-reminder");
      toast.success(`Party-Mail an Gäste: ${data.sent} gesendet, ${data.failed} fehlgeschlagen.`);
    } catch (error) {
      toast.error(formatApiError(error, "Party-Mail konnte nicht gesendet werden."));
    } finally {
      setReminderLoading(false);
    }
  };

  if (user === undefined) {
    return <main data-testid="admin-loading-state" className="grid min-h-screen place-items-center bg-[#09090D] text-[#E5C378]">Wird geladen...</main>;
  }

  if (!user) {
    return (
      <main className="relative grid min-h-screen place-items-center overflow-hidden bg-[#09090D] px-5 text-white">
        <div className="hero-grid absolute inset-0 opacity-35" />
        <form data-testid="admin-login-form" onSubmit={login} className="relative w-full max-w-md rounded-[2rem] border border-[#E5C378]/20 bg-[#13131A]/90 p-8 shadow-2xl shadow-black/50 backdrop-blur-xl">
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#E5C378]">Veranstalter-Anmeldung</p>
          <h1 className="mt-4 font-syne text-4xl font-bold">Vyora Kontrollpanel</h1>
          <div className="mt-8 space-y-5">
            <Input data-testid="admin-email-input" type="email" placeholder="E-Mail" value={loginForm.email} onChange={(event) => setLoginForm({ ...loginForm, email: event.target.value })} required className="h-12 rounded-2xl border-white/10 bg-black/25 text-white" />
            <Input data-testid="admin-password-input" type="password" placeholder="Passwort" value={loginForm.password} onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })} required className="h-12 rounded-2xl border-white/10 bg-black/25 text-white" />
            <Button data-testid="admin-login-button" disabled={loading} className="h-12 w-full rounded-2xl bg-[#E5C378] font-bold text-[#09090D] hover:bg-[#f0cf8a]">{loading ? "Anmeldung läuft..." : "Zum Panel anmelden"}</Button>
          </div>
          <Link data-testid="admin-back-home-link" to="/" className="mt-6 inline-block text-sm text-zinc-500 transition-colors duration-200 hover:text-[#E5C378]">Zur Website</Link>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#09090D] px-5 py-8 text-white sm:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col justify-between gap-5 rounded-[2rem] border border-[#E5C378]/20 bg-[#13131A] p-6 md:flex-row md:items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#E5C378]">Veranstalterbereich</p>
            <h1 className="mt-2 font-syne text-4xl font-bold">Vyora Pilot Check-in</h1>
            <p data-testid="admin-user-info" className="mt-2 text-sm text-zinc-400">{user.name} • {user.email}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="admin-home-link" to="/" className="rounded-full border border-white/10 px-5 py-3 text-sm text-zinc-300 transition-colors duration-200 hover:border-[#E5C378]/50 hover:text-[#E5C378]">Website öffnen</Link>
            <button data-testid="refresh-registrations-button" type="button" onClick={() => { loadRegistrations(); loadWaitlist(); }} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-sm text-zinc-300 transition-colors duration-200 hover:border-[#E5C378]/50 hover:text-[#E5C378]"><RefreshCcw className="h-4 w-4" /> Aktualisieren</button>
            <a data-testid="export-csv-button" href={`${API_BASE_URL}/admin/registrations/export.csv`} className="inline-flex items-center gap-2 rounded-full border border-[#E5C378]/25 px-5 py-3 text-sm font-bold text-[#E5C378] transition-colors duration-200 hover:bg-[#E5C378]/10"><Download className="h-4 w-4" /> CSV</a>
            <button data-testid="admin-logout-button" type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-3 text-sm text-zinc-200 transition-colors duration-200 hover:bg-white/15"><LogOut className="h-4 w-4" /> Abmelden</button>
          </div>
        </header>

        <section className="mt-8 grid gap-4 md:grid-cols-3">
          {[["Tickets", stats.total, Users], ["Am Eingang bezahlt", stats.paid, ShieldCheck], ["Eingecheckt", stats.checked, ClipboardCheck]].map(([label, value, Icon]) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`} className="rounded-[1.5rem] border border-white/10 bg-[#13131A] p-6">
              <Icon className="h-5 w-5 text-[#E5C378]" />
              <p className="mt-5 text-sm text-zinc-500">{label}</p>
              <p className="mt-2 font-syne text-4xl font-extrabold">{value}</p>
            </div>
          ))}
        </section>

        <section className="mt-8 grid gap-8 lg:grid-cols-[0.72fr_1.28fr]">
          <form data-testid="qr-checkin-form" onSubmit={submitCheckin} className="h-fit rounded-[2rem] border border-[#E5C378]/20 bg-[#13131A] p-6">
            <div className="flex items-center gap-3">
              <ScanLine className="h-6 w-6 text-[#E5C378]" />
              <h2 className="font-syne text-2xl font-bold">QR-Check-in</h2>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-zinc-500">Kamera starten oder Code einfügen. Beim Check-in wird die Zahlung am Eingang automatisch als erhalten markiert.</p>
            <div className="mt-6 overflow-hidden rounded-3xl border border-white/10 bg-black/25">
              <div id="qr-camera-reader" data-testid="qr-camera-reader" className={scannerActive ? "min-h-64 w-full" : "hidden"} />
              {!scannerActive && <div className="grid min-h-56 place-items-center p-6 text-center text-sm text-zinc-500">Kamera ist bereit. Starten Sie den Scan am Eingang.</div>}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="start-camera-scanner-button" type="button" onClick={startScanner} disabled={scannerActive} className="inline-flex items-center gap-2 rounded-full border border-[#E5C378]/25 px-5 py-3 text-sm font-bold text-[#E5C378] transition-colors duration-200 hover:bg-[#E5C378]/10 disabled:cursor-not-allowed disabled:opacity-40">
                <Camera className="h-4 w-4" /> Kamera starten
              </button>
              {scannerActive && <button data-testid="stop-camera-scanner-button" type="button" onClick={stopScanner} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-sm text-zinc-300 transition-colors duration-200 hover:border-red-400/40 hover:text-red-300"><CameraOff className="h-4 w-4" /> Kamera stoppen</button>}
            </div>
            {scannerError && <p data-testid="camera-scanner-error" className="mt-3 rounded-2xl border border-amber-400/25 bg-amber-400/10 p-3 text-sm text-amber-200">{scannerError}</p>}
            <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-600"><span className="h-px flex-1 bg-white/10" />oder manuell<span className="h-px flex-1 bg-white/10" /></div>
            <Input data-testid="qr-checkin-input" value={checkinCode} onChange={(event) => setCheckinCode(event.target.value)} placeholder="VY-XXXXXXXX oder QR-Inhalt" required className="h-12 rounded-2xl border-white/10 bg-black/25 text-white" />
            <Button data-testid="checkin-confirm-button" className="mt-4 h-12 w-full rounded-2xl bg-[#E5C378] font-bold text-[#09090D] hover:bg-[#f0cf8a]">Einlass + Zahlung bestätigen</Button>
            {checkinResult && (
              <div data-testid="checkin-result" className="mt-5 rounded-2xl border border-white/10 bg-black/25 p-4 text-sm">
                <p className="font-semibold text-white">{checkinResult.registration.full_name}</p>
                <p className="mt-1 text-zinc-400">{checkinResult.registration.registration_code}</p>
                <p className={checkinResult.already_checked_in ? "mt-3 text-amber-300" : "mt-3 text-emerald-300"}>{checkinResult.already_checked_in ? "Bereits eingecheckt" : `Einlass bestätigt • €${checkinResult.registration.price_eur} am Eingang`}</p>
              </div>
            )}
          </form>

          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#13131A]">
            <div className="flex flex-col justify-between gap-4 border-b border-white/10 p-6 sm:flex-row sm:items-center">
              <h2 className="font-syne text-2xl font-bold">Registrierte Gäste</h2>
              <button data-testid="send-reminder-button" type="button" onClick={sendEventReminder} disabled={reminderLoading || registrations.length === 0} className="inline-flex items-center gap-2 rounded-full border border-[#E5C378]/25 px-5 py-3 text-sm font-bold text-[#E5C378] transition-colors duration-200 hover:bg-[#E5C378]/10 disabled:cursor-not-allowed disabled:opacity-40">
                <PartyPopper className="h-4 w-4" /> {reminderLoading ? "Wird gesendet..." : "Party-Mail an alle Gäste"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table data-testid="attendee-list-table" className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.16em] text-zinc-500">
                  <tr><th className="px-6 py-4">Gast</th><th className="px-6 py-4">Code</th><th className="px-6 py-4">Zahlung</th><th className="px-6 py-4">QR-Ticket</th><th className="px-6 py-4">Check-in</th><th className="px-6 py-4">Aktion</th></tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {registrations.map((registration) => (
                    <tr key={registration._id || registration.registration_code} data-testid={`attendee-row-${registration.registration_code}`} className="text-zinc-300">
                      <td className="px-6 py-4"><p className="font-semibold text-white">{registration.full_name}</p><p className="text-xs text-zinc-500">{registration.email}</p></td>
                      <td className="px-6 py-4 font-mono text-[#E5C378]">{registration.registration_code}</td>
                      <td className="px-6 py-4"><span className={registration.payment_status === "paid_at_door" ? "rounded-full bg-emerald-400/10 px-3 py-1 text-emerald-300" : "rounded-full bg-amber-400/10 px-3 py-1 text-amber-300"}>€{registration.price_eur} • {registration.payment_status === "paid_at_door" ? "am Eingang bezahlt" : "am Eingang offen"}</span></td>
                      <td className="px-6 py-4"><span className={registration.qr_email_sent ? "rounded-full bg-emerald-400/10 px-3 py-1 text-emerald-300" : "rounded-full bg-white/10 px-3 py-1 text-zinc-400"}>{registration.qr_email_sent ? "Gesendet" : "Nicht gesendet"}</span></td>
                      <td className="px-6 py-4">{registration.checked_in ? <span className="inline-flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-4 w-4" /> {formatDate(registration.checked_in_at)}</span> : "Offen"}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {registration.payment_status !== "paid_at_door" && <button data-testid={`mark-paid-${registration.registration_code}`} type="button" onClick={() => markPaid(registration.registration_code)} className="rounded-full border border-[#E5C378]/25 px-4 py-2 text-xs font-bold text-[#E5C378] transition-colors duration-200 hover:bg-[#E5C378]/10">Zahlung OK</button>}
                          {!registration.qr_email_sent && <button data-testid={`send-boarding-pass-${registration.registration_code}`} type="button" onClick={() => sendBoardingPass(registration.registration_code)} className="rounded-full border border-emerald-400/25 px-4 py-2 text-xs font-bold text-emerald-300 transition-colors duration-200 hover:bg-emerald-400/10">QR senden</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {registrations.length === 0 && <p data-testid="empty-attendee-list" className="p-8 text-center text-zinc-500">Noch keine Tickets.</p>}
            </div>
          </div>
        </section>

        <section data-testid="waitlist-admin-panel" className="mt-8 rounded-[2rem] border border-white/10 bg-[#13131A] p-6">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#E5C378]">Warteliste</p>
              <h2 className="mt-2 font-syne text-2xl font-bold">Interessenten für neue Tickets</h2>
            </div>
            <button data-testid="notify-waitlist-button" type="button" onClick={notifyWaitlist} disabled={waitlist.length === 0} className="inline-flex items-center gap-2 rounded-full border border-[#E5C378]/25 px-5 py-3 text-sm font-bold text-[#E5C378] transition-colors duration-200 hover:bg-[#E5C378]/10 disabled:cursor-not-allowed disabled:opacity-40">
              <BellRing className="h-4 w-4" /> Alle erneut benachrichtigen
            </button>
          </div>
          <div className="mt-6 divide-y divide-white/10">
            {waitlist.map((entry) => (
              <div key={entry._id || entry.email} data-testid={`waitlist-row-${entry.email}`} className="flex flex-col justify-between gap-2 py-4 text-sm sm:flex-row sm:items-center">
                <div><p className="font-semibold text-white">{entry.full_name}</p><p className="text-zinc-500">{entry.email}</p></div>
                <p className="text-zinc-400">{entry.notified_count > 0 ? `Benachrichtigt (${entry.notified_count}x)` : "Noch nicht benachrichtigt"}</p>
              </div>
            ))}
            {waitlist.length === 0 && <p data-testid="empty-waitlist" className="py-6 text-center text-zinc-500">Warteliste ist leer.</p>}
          </div>
        </section>
      </div>
    </main>
  );
}
